import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next'
import React from 'react'
import { useRecoilValue } from 'recoil'
import { Breadcrumbs } from '../components/Breadcrumbs'
import { Button } from '../components/Button'
import { Content } from '../components/Content'
import { Intro } from '../components/Intro'
import { MissingContent } from '../components/MissingContent'
import { PrintButton } from '../components/PrintButton'
import { ProductList, ProductListKind } from '../components/ProductList'
import { Seo } from '../components/Seo'
import { ShareShoppingListButton } from '../components/ShareShoppingListButton'
import { ShoppingListLayout } from '../components/ShoppingListLayout'
import { WidthBound } from '../components/WidthBound'
import { useFetchShoppingList } from '../src/hooks/useFetchShoppingList'
import { useLocalization } from '../src/hooks/useLocaleFormatter'
import { usePageLink } from '../src/hooks/usePageLink'
import { useTranslate } from '../src/hooks/useTranslate'
import { Currency, ProductPriceRegion } from '../src/model/currency'
import { PageFragment, ProductUnit, ProductVariantFragment, SpecialPageType } from '../src/model/fragments'
import {
	DEFAULT_LOCALE,
	getLocale,
	getPossibleLocales,
	Locale,
} from '../src/model/locale'
import { shoppingListState } from '../src/store/shoppingListState'
import { axiosGet } from '../src/utils/axiosWrapper'

export default function ShoppingList({
	page,
	locales,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
	const inquiryLink = usePageLink(SpecialPageType.shoppingListInquiry)!
	const translate = useTranslate()
	const localization = useLocalization()

	const storedData = useRecoilValue(shoppingListState);
	const shoppingList = useFetchShoppingList(storedData);

	const items = React.useMemo(() => {

		if(!shoppingList.data)
		{
			return [];
		}

		return shoppingList.data!.items;

	}, [shoppingList, storedData]);

	const prices = React.useMemo(() =>
			localization.extractPrices(
				items.reduce(
					(previous, current) => {
						return previous.map((priceBox) => {
							const variant = current.product.variants.find(v => v.id == current.variantId) as ProductVariantFragment;
							const variantPrice = variant?.prices.find(vpr => vpr.currency == priceBox.currency && vpr.region == priceBox.region);
							if(!variantPrice)
							{
								return priceBox;
							}

							
							let multiplier = parseFloat(current.count.toString());
							// if(current.product.attributes.unit == ProductUnit.sqm)
							// {
							// 	const quantityPerSqm = variant.attributes.quantityPerSqm ?? (variant.attributes.palletLayerQuantity / variant.attributes.palletLayerSqm)
							// 	multiplier = 1.0 / quantityPerSqm;
							// }

							const addition = variantPrice.cents * multiplier;
							return {
								...priceBox,
								cents: priceBox.cents + addition
							}
						})
					},
					[
						{ currency: Currency.CZK, cents: 0, region: ProductPriceRegion.CZ },
						{ currency: Currency.EUR, cents: 0, region: ProductPriceRegion.SK }
					],
				),
			),
		[localization, items],
	)

	if (!page) {
		return <MissingContent />
	}

	const data = getLocale(page.locales, locales)

	if (!data) {
		return <MissingContent />
	}

	return (
		<>
			<Seo seo={data.seo} />
			<Breadcrumbs
				items={[
					{
						label: data.title,
					},
				]}
			/>
			<Intro title={data.title}>
				<WidthBound size="narrow">
					<Content content={data.content} innerSize="normal" />
				</WidthBound>
			</Intro>
			<ShoppingListLayout
				productList={
					<ProductList
						items={items.map(it => ({
							...it.product,
							controlledQuantity: it.count,
							controlledVariantId: it.variantId,
							shoppingListVariantId: it.variantId
						}))}
						kind={ProductListKind.ShoppingList}
						note={shoppingList.error ? shoppingList.error : (shoppingList.loading ? translate('shoppingList-stahuji-se-data') : undefined)}
					/>
				}
				submit={
					<Button linkProps={inquiryLink} block>
						{translate('send-inquiry')}
					</Button>
				}
				actions={
					<>
						<PrintButton>{translate('shoppingList-print-label')}</PrintButton>
						<ShareShoppingListButton>
							{translate('shoppingList-share-label')}
						</ShareShoppingListButton>
					</>
				}
				prices={prices}
			/>
		</>
	)
}

export const getServerSideProps = async (ctx: GetServerSidePropsContext) => {
	const locale = (ctx.query.locale || DEFAULT_LOCALE) as Locale
	const locales = getPossibleLocales(ctx.query.locale as Locale)

	const { getPage: page } = await axiosGet('/layout/page/get', {locale: locale, navigationId: ctx.query.id}) as {
		getPage: PageFragment
	}
	
	return { props: { page, locales } }
}
